<script>
import AuthWrapper from '@/components/auth/AuthWrapper.vue';

const MIN_LENGTH = 8;

export default {
  name: 'PasswordSetupForm',
  components: {
    AuthWrapper,
  },
  props: {
    title: { type: String, required: true },
    subtitle: { type: String, required: true },
    submitLabel: { type: String, required: true },
    successTitle: { type: String, required: true },
    successBody: { type: String, required: true },
    /** Verification code from the email link; absent means the link is unusable. */
    code: { type: String, default: null },
    /**
     * Le formulaire s'ouvre-t-il depuis un lien reçu par courriel ?
     *
     * Le changement de mot de passe temporaire, lui, part d'une session déjà
     * ouverte : il n'y a pas de code à vérifier, et exiger celui du courriel
     * garderait le bouton désactivé pour toujours.
     */
    requiresCode: { type: Boolean, default: true },
    /**
     * Le libellé du champ « mot de passe actuel ». Absent, le champ ne
     * s'affiche pas : un lien de réinitialisation prouve déjà l'identité, une
     * session ouverte ne prouve que l'accès au poste.
     */
    currentPasswordLabel: { type: String, default: null },
    processing: { type: Boolean, default: false },
    apiError: { type: String, default: null },
    success: { type: Boolean, default: false },
  },
  emits: ['submit'],
  data() {
    return {
      currentPassword: '',
      password: '',
      passwordConfirm: '',
      showCurrent: false,
      showPassword: false,
      showConfirm: false,
      errors: {
        current: null,
        password: null,
        confirm: null,
      },
    };
  },
  methods: {
    validate() {
      const errors = { current: null, password: null, confirm: null };

      if (this.currentPasswordLabel && !this.currentPassword) {
        errors.current = 'Veuillez saisir votre mot de passe actuel.';
      }

      if (!this.password) {
        errors.password = 'Veuillez saisir un mot de passe.';
      } else if (this.password.length < MIN_LENGTH) {
        errors.password =
          'Le mot de passe doit contenir au moins ' +
          MIN_LENGTH +
          ' caractères.';
      }

      if (!this.passwordConfirm) {
        errors.confirm = 'Veuillez confirmer le mot de passe.';
      } else if (this.password !== this.passwordConfirm) {
        errors.confirm = 'Les mots de passe ne correspondent pas.';
      }

      this.errors = errors;

      return !errors.current && !errors.password && !errors.confirm;
    },

    handleSubmit() {
      if (!this.validate()) {
        return;
      }

      // Le mot de passe actuel vient en second: les appelants qui n'en
      // demandent pas continuent de ne lire que le premier argument.
      this.$emit('submit', this.password, this.currentPassword);
    },

    goToLogin() {
      this.$router.push({ path: '/login' });
    },
  },
};
</script>

<template>
  <AuthWrapper variant="form">
    <BCard no-body class="auth-page__card">
      <BCardBody class="auth-page__card-body">
        <div v-if="success" class="auth-form__result">
          <div class="auth-form__icon">
            <i class="ri-shield-check-line"></i>
          </div>
          <h1 class="auth-form__title">{{ successTitle }}</h1>
          <p class="auth-form__body">{{ successBody }}</p>
          <button class="auth-form__submit" type="button" @click="goToLogin">
            Aller à la connexion
          </button>
        </div>

        <div v-else>
          <div v-if="apiError" class="auth-form__alert" role="alert">
            {{ apiError }}
          </div>

          <div
            v-if="requiresCode && !code"
            class="auth-form__alert auth-form__alert--warning"
            role="alert"
          >
            Code manquant. Veuillez utiliser le lien reçu par e-mail.
          </div>

          <div class="text-center">
            <div class="auth-page__logo mb-3">
              <img src="@/assets/images/logo_apii.png" alt="Logo APII" />
            </div>
            <h1 class="auth-form__title">{{ title }}</h1>
            <p class="auth-form__subtitle">{{ subtitle }}</p>
          </div>

          <form novalidate @submit.prevent="handleSubmit">
            <div class="auth-form__fields">
              <div v-if="currentPasswordLabel" class="auth-form__field">
                <label class="auth-form__label" for="currentPassword">
                  {{ currentPasswordLabel }}
                </label>
                <div class="position-relative">
                  <input
                    id="currentPassword"
                    v-model="currentPassword"
                    :type="showCurrent ? 'text' : 'password'"
                    class="form-control auth-form__password-input"
                    :class="{ 'is-invalid': errors.current }"
                    :aria-invalid="Boolean(errors.current)"
                    placeholder="Entrez votre mot de passe actuel"
                    autocomplete="current-password"
                    data-testid="current-password"
                  />
                  <button
                    type="button"
                    class="auth-form__password-toggle"
                    :aria-label="
                      showCurrent
                        ? 'Masquer le mot de passe'
                        : 'Afficher le mot de passe'
                    "
                    :aria-pressed="showCurrent"
                    @click="showCurrent = !showCurrent"
                  >
                    <i
                      :class="showCurrent ? 'ri-eye-off-fill' : 'ri-eye-fill'"
                    ></i>
                  </button>
                </div>
                <span v-if="errors.current" class="auth-form__error">
                  {{ errors.current }}
                </span>
              </div>

              <div class="auth-form__field">
                <label class="auth-form__label" for="password"
                  >Nouveau mot de passe</label
                >
                <div class="position-relative">
                  <input
                    id="password"
                    v-model="password"
                    :type="showPassword ? 'text' : 'password'"
                    class="form-control auth-form__password-input"
                    :class="{ 'is-invalid': errors.password }"
                    :aria-invalid="Boolean(errors.password)"
                    placeholder="Entrez votre nouveau mot de passe"
                    autocomplete="new-password"
                  />
                  <button
                    type="button"
                    class="auth-form__password-toggle"
                    :aria-label="
                      showPassword
                        ? 'Masquer le mot de passe'
                        : 'Afficher le mot de passe'
                    "
                    :aria-pressed="showPassword"
                    @click="showPassword = !showPassword"
                  >
                    <i
                      :class="showPassword ? 'ri-eye-off-fill' : 'ri-eye-fill'"
                    ></i>
                  </button>
                </div>
                <span v-if="errors.password" class="auth-form__error">
                  {{ errors.password }}
                </span>
              </div>

              <div class="auth-form__field">
                <label class="auth-form__label" for="passwordConfirm"
                  >Confirmer le mot de passe</label
                >
                <div class="position-relative">
                  <input
                    id="passwordConfirm"
                    v-model="passwordConfirm"
                    :type="showConfirm ? 'text' : 'password'"
                    class="form-control auth-form__password-input"
                    :class="{ 'is-invalid': errors.confirm }"
                    :aria-invalid="Boolean(errors.confirm)"
                    placeholder="Confirmez votre nouveau mot de passe"
                    autocomplete="new-password"
                  />
                  <button
                    type="button"
                    class="auth-form__password-toggle"
                    :aria-label="
                      showConfirm
                        ? 'Masquer le mot de passe'
                        : 'Afficher le mot de passe'
                    "
                    :aria-pressed="showConfirm"
                    @click="showConfirm = !showConfirm"
                  >
                    <i
                      :class="showConfirm ? 'ri-eye-off-fill' : 'ri-eye-fill'"
                    ></i>
                  </button>
                </div>
                <span v-if="errors.confirm" class="auth-form__error">
                  {{ errors.confirm }}
                </span>
              </div>
            </div>

            <button
              class="auth-form__submit"
              type="submit"
              :disabled="processing || (requiresCode && !code)"
              data-testid="password-submit"
            >
              {{ processing ? 'En cours…' : submitLabel }}
            </button>

            <!-- Le pied de page: un lien vers la connexion par défaut, autre
                 chose quand l'écran s'ouvre depuis une session. -->
            <p class="auth-form__footnote">
              <slot name="footnote">
                <router-link to="/login" class="auth-form__link">
                  Retour à la connexion
                </router-link>
              </slot>
            </p>
          </form>
        </div>
      </BCardBody>
    </BCard>
  </AuthWrapper>
</template>
